/** Owns replay-safe Control UI execution around the lazy report consent flow. */
import type { GatewayBrowserClient } from "../api/gateway.ts";
import { formatUiError } from "../lib/format-error.ts";
import { canCallGatewayMethod } from "../lib/gateway-methods.ts";
import type { ApplicationGatewaySnapshot } from "./gateway.ts";
import type { SubmittedUpdateReport } from "./update-failure-report.ts";

type ReportResult = SubmittedUpdateReport | { message: string; status: "error" };

export function canReportUpdateFailure(snapshot: ApplicationGatewaySnapshot): boolean {
  // The Gateway selects browser handoff versus host publication; eligibility
  // here never grants access to the host's GitHub account.
  return (
    Boolean(snapshot.selfUser?.id?.trim()) &&
    canCallGatewayMethod(snapshot, "update.report", "operator.admin", {
      requireAdvertisement: false,
    })
  );
}

export function createUpdateFailureReportController(params: {
  getClient: () => GatewayBrowserClient | null;
  isCurrent: (attemptId: string, client: GatewayBrowserClient) => boolean;
  setBusy: (busy: boolean) => void;
  setResult: (attemptId: string, result: ReportResult) => void;
}) {
  let activeRequest: object | null = null;

  return {
    invalidate: () => {
      activeRequest = null;
    },
    report: async (attemptId: string): Promise<void> => {
      const client = params.getClient();
      if (!client || activeRequest !== null || !params.isCurrent(attemptId, client)) {
        return;
      }
      const request = {};
      activeRequest = request;
      const isCurrent = () => activeRequest === request && params.isCurrent(attemptId, client);
      params.setBusy(true);
      try {
        const { reportUpdateFailure } = await import("./update-failure-report.ts");
        const result = await reportUpdateFailure({ attemptId, client, isCurrent });
        if (result && isCurrent()) {
          params.setResult(attemptId, result);
        }
      } catch (error) {
        if (isCurrent()) {
          params.setResult(attemptId, { status: "error", message: formatUiError(error) });
        }
      } finally {
        if (activeRequest === request) {
          activeRequest = null;
          params.setBusy(false);
        }
      }
    },
  };
}
